import { useState } from "react";
import { Link } from "react-router-dom";
import { TextInput } from "../components/Inputs";
import { useLoaderStore } from "../context";
import { useAlert, useApi } from "../hooks";
import { API } from "../utils/constants";

/**
 * ForgotPassword — Request a password reset (SPR-008 FE-5, unauth).
 *
 * Flow (CON-002 §1):
 * - POST /api/auth/forgot-password { email }
 * - Always shows a NEUTRAL, enumeration-safe confirmation ("if an account
 *   exists, a reset link was sent") regardless of whether the email matched.
 * - 422 (malformed input) surfaces an inline validation message.
 * Linked from Login.js.
 */
export default function ForgotPassword() {
  const alert = useAlert();
  const { post } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      alert("Invalid input", "Please enter your email address.", "warning");
      return;
    }
    setShowLoader(true);
    try {
      await post(API.AUTH.FORGOT_PASSWORD, { email: email.trim() });
      // Enumeration-safe: always show the same neutral confirmation.
      setSubmitted(true);
    } catch (err) {
      if (err?.status === 422) {
        alert("Invalid input", err?.data?.detail || "Please enter a valid email address.", "warning");
      } else {
        // Even on unexpected errors we avoid leaking account existence; show the
        // neutral confirmation. (A true server outage would still toast nothing
        // sensitive.)
        setSubmitted(true);
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cream flex items-center justify-center px-4"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl text-charcoal leading-tight">
            Reset Password
          </h1>
          <p className="font-body text-charcoal/60 mt-2 text-sm">Admin Portal</p>
        </div>

        <div className="bg-white border border-aluminum/30 rounded-sm shadow-sm p-8 space-y-5">
          {submitted ? (
            <div className="space-y-4" data-testid="forgot-password-confirmation">
              <h2 className="font-heading text-xl text-charcoal tracking-wider">CHECK YOUR EMAIL</h2>
              <p className="font-body text-charcoal/70 text-sm">
                If an account exists for that email, a password reset link has been sent.
              </p>
              <Link
                to="/login"
                className="block text-center w-full bg-teal text-white font-heading tracking-widest text-lg py-2.5 rounded-sm hover:bg-teal/80 transition-all"
                data-testid="back-to-login-link"
              >
                BACK TO SIGN IN
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl text-charcoal tracking-wider">FORGOT PASSWORD</h2>
              <p className="font-body text-charcoal/60 text-sm">
                Enter your email and we'll send you a link to reset your password.
              </p>

              <div>
                <TextInput
                  label="Email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  data-testid="email-input"
                />
              </div>

              <button
                onClick={handleSubmit}
                className="w-full bg-teal text-white font-heading tracking-widest text-lg py-2.5 rounded-sm hover:bg-teal/80 active:scale-95 transition-all"
                data-testid="forgot-password-button"
              >
                SEND RESET LINK
              </button>

              <Link
                to="/login"
                className="block text-center font-heading tracking-wider text-sm text-teal hover:text-teal/70 transition-colors"
                data-testid="back-to-login-link"
              >
                BACK TO SIGN IN
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
